// public-frontend/src/components/ContactUs/ContactUs.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Mail,
  Send,
  CheckCircle,
  AlertCircle,
  User,
  MessageSquare,
  Loader,
  Shield,
  Clock,
} from 'lucide-react';
import { log } from '../utils/logger';

interface ContactUsProps {
  dark: boolean;
  apiBase?: string;
}

interface FormData {
  fullName: string;
  email: string;
  message: string;
}

interface FormErrors {
  fullName?: string;
  email?: string;
  message?: string;
  general?: string;
}

const ContactUs: React.FC<ContactUsProps> = ({ dark, apiBase }) => {
  // Form state
  const [formData, setFormData] = useState<FormData>({
    fullName: "",
    email: "",
    message: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Robot protection
  const [honeypot, setHoneypot] = useState(""); // Honeypot field (should stay empty)
  const [formOpenTime, setFormOpenTime] = useState<number>(0);
  const [canSubmit, setCanSubmit] = useState(false);
  const submitAttempts = useRef(0);
  const lastSubmitTime = useRef(0);

  // URLs come from the `apiBase` prop (App.tsx → VITE_API_URL ?? ''), which is
  // empty in both dev and prod so '/api/contact' stays same-origin. Do not
  // reintroduce a local `VITE_API_BASE || "http://localhost:3001"` constant —
  // that spelling ships a localhost URL to every visitor's browser.

  // Track when form was opened (bot protection)
  useEffect(() => {
    setFormOpenTime(Date.now());
    // Enable submit after 2 seconds (humans need time to fill form)
    const timer = setTimeout(() => {
      setCanSubmit(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // Auto-clear success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(false);
        resetForm();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Validation
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Name validation
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Name is required";
    } else if (formData.fullName.trim().length < 2) {
      newErrors.fullName = "Name must be at least 2 characters";
    } else if (formData.fullName.trim().length > 100) {
      newErrors.fullName = "Name is too long";
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    } else if (formData.message.trim().length > 5000) {
      newErrors.message = "Message is too long (max 5000 characters)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Robot protection checks
  const passesRobotProtection = (): boolean => {
    // Check 1: Honeypot field must be empty
    if (honeypot !== "") {
      log.warn('ContactUs', 'Robot protection: Honeypot field filled');
      return false;
    }

    // Check 2: Form must be open for at least 2 seconds
    const timeSinceOpen = Date.now() - formOpenTime;
    if (timeSinceOpen < 2000) {
      log.warn('ContactUs', 'Robot protection: Form submitted too quickly');
      return false;
    }

    // Check 3: Rate limiting (max 3 attempts per 60 seconds)
    const now = Date.now();
    if (now - lastSubmitTime.current < 20000) {
      // 20 seconds between submissions
      submitAttempts.current++;
      if (submitAttempts.current > 3) {
        setSubmitError("Too many submission attempts. Please wait a moment.");
        return false;
      }
    } else {
      submitAttempts.current = 1;
    }
    lastSubmitTime.current = now;

    return true;
  };

  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      fullName: "",
      email: "",
      message: "",
    });
    setErrors({});
    setHoneypot("");
    submitAttempts.current = 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setSubmitError(null);

    // Validate form
    if (!validateForm()) {
      return;
    }

    // Robot protection
    if (!passesRobotProtection()) {
      setSubmitError("Please wait a moment before submitting.");
      return;
    }

    if (!canSubmit) {
      setSubmitError("Please wait a moment before submitting.");
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(`${apiBase}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.fullName.trim(),
          email: formData.email.trim(),
          subject: "Contact Form Submission", // Default subject
          message: formData.message.trim(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send message");
      }

      // Success!
      setSuccess(true);
      setLoading(false);
    } catch (err: any) {
      log.error('ContactUs', 'Error submitting contact form', { err: String(err) });
      setSubmitError(err.message || "Failed to send message. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div
      className={`min-h-screen py-12 px-4 ${
        dark ? "bg-gray-900 text-white" : "bg-gray-50 text-gray-900"
      }`}
    >
      <div className='max-w-4xl mx-auto'>
        {/* Header */}
        <div className='text-center mb-12'>
          <div className='flex items-center justify-center mb-4'>
            <div className={`p-4 rounded-full ${dark ? "bg-blue-900/30" : "bg-blue-100"}`}>
              <Mail className='w-12 h-12 text-gray-500' />
            </div>
          </div>
          <h1 className={`text-4xl font-bold mb-4 ${dark ? "text-gray-100" : "text-gray-900"}`}>
            Contact Us
          </h1>
          <p className={`text-lg ${dark ? "text-gray-400" : "text-gray-600"}`}>
            Have a question or feedback? We'd love to hear from you.
          </p>
        </div>

        {/* Success Message */}
        {success && (
          <div className='mb-8 animate-fade-in'>
            <div
              className={`rounded-xl p-6 border ${
                dark ? "bg-green-900/20 border-green-500/30" : "bg-green-50 border-green-200"
              }`}
            >
              <div className='flex items-start gap-4'>
                <div className={`p-2 rounded-full ${dark ? "bg-green-500/20" : "bg-green-100"}`}>
                  <CheckCircle className='w-6 h-6 text-green-500' />
                </div>
                <div className='flex-1'>
                  <h3
                    className={`text-lg font-bold mb-2 ${
                      dark ? "text-green-300" : "text-green-900"
                    }`}
                  >
                    Message Sent Successfully!
                  </h3>
                  <p className={`${dark ? "text-green-200" : "text-green-800"}`}>
                    Thank you for contacting us. We'll get back to you as soon as possible.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {submitError && (
          <div className='mb-8 animate-fade-in'>
            <div
              className={`rounded-xl p-6 border ${
                dark ? "bg-red-900/20 border-red-500/30" : "bg-red-50 border-red-200"
              }`}
            >
              <div className='flex items-start gap-4'>
                <div className={`p-2 rounded-full ${dark ? "bg-red-500/20" : "bg-red-100"}`}>
                  <AlertCircle className='w-6 h-6 text-red-500' />
                </div>
                <div className='flex-1'>
                  <h3
                    className={`text-lg font-bold mb-2 ${dark ? "text-red-300" : "text-red-900"}`}
                  >
                    Error Sending Message
                  </h3>
                  <p className={`${dark ? "text-red-200" : "text-red-800"}`}>{submitError}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Contact Form */}
        <div
          className={`rounded-xl p-8 border shadow-lg ${
            dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
          }`}
        >
          <form onSubmit={handleSubmit} className='space-y-6'>
            {/* Full Name */}
            <div>
              <label
                htmlFor='fullName'
                className={`block text-sm font-medium mb-2 ${
                  dark ? "text-gray-200" : "text-gray-700"
                }`}
              >
                Full Name *
              </label>
              <div className='relative'>
                <div className='absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none'>
                  <User className={`w-5 h-5 ${dark ? "text-gray-500" : "text-gray-400"}`} />
                </div>
                <input
                  type='text'
                  id='fullName'
                  name='fullName'
                  value={formData.fullName}
                  onChange={handleChange}
                  disabled={loading || success}
                  className={`w-full pl-10 pr-4 py-3 rounded-lg border transition-all ${
                    errors.fullName
                      ? "border-red-500 focus:ring-red-500"
                      : dark
                        ? "bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500"
                  } ${loading || success ? "opacity-50 cursor-not-allowed" : ""}`}
                  placeholder='John Doe'
                  maxLength={100}
                />
              </div>
              {errors.fullName && (
                <p className='mt-1 text-sm text-red-500 flex items-center gap-1'>
                  <AlertCircle className='w-4 h-4' />
                  {errors.fullName}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label
                htmlFor='email'
                className={`block text-sm font-medium mb-2 ${
                  dark ? "text-gray-200" : "text-gray-700"
                }`}
              >
                Email Address *
              </label>
              <div className='relative'>
                <div className='absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none'>
                  <Mail className={`w-5 h-5 ${dark ? "text-gray-500" : "text-gray-400"}`} />
                </div>
                <input
                  type='email'
                  id='email'
                  name='email'
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading || success}
                  className={`w-full pl-10 pr-4 py-3 rounded-lg border transition-all ${
                    errors.email
                      ? "border-red-500 focus:ring-red-500"
                      : dark
                        ? "bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500"
                  } ${loading || success ? "opacity-50 cursor-not-allowed" : ""}`}
                  placeholder='john@example.com'
                />
              </div>
              {errors.email && (
                <p className='mt-1 text-sm text-red-500 flex items-center gap-1'>
                  <AlertCircle className='w-4 h-4' />
                  {errors.email}
                </p>
              )}
            </div>

            {/* Message */}
            <div>
              <label
                htmlFor='message'
                className={`block text-sm font-medium mb-2 ${
                  dark ? "text-gray-200" : "text-gray-700"
                }`}
              >
                Message *
              </label>
              <div className='relative'>
                <div className='absolute left-3 top-3 pointer-events-none'>
                  <MessageSquare
                    className={`w-5 h-5 ${dark ? "text-gray-500" : "text-gray-400"}`}
                  />
                </div>
                <textarea
                  id='message'
                  name='message'
                  value={formData.message}
                  onChange={handleChange}
                  disabled={loading || success}
                  rows={6}
                  className={`w-full pl-10 pr-4 py-3 rounded-lg border transition-all ${
                    errors.message
                      ? "border-red-500 focus:ring-red-500"
                      : dark
                        ? "bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500"
                  } ${loading || success ? "opacity-50 cursor-not-allowed" : ""}`}
                  placeholder='Tell us how we can help you...'
                  maxLength={5000}
                />
              </div>
              <div className='flex items-center justify-between mt-1'>
                {errors.message ? (
                  <p className='text-sm text-red-500 flex items-center gap-1'>
                    <AlertCircle className='w-4 h-4' />
                    {errors.message}
                  </p>
                ) : (
                  <div />
                )}
                <span className={`text-xs ${dark ? "text-gray-500" : "text-gray-400"}`}>
                  {formData.message.length} / 5000
                </span>
              </div>
            </div>

            {/* Honeypot field (hidden from users, visible to bots) */}
            <div className='hidden' aria-hidden='true'>
              <label htmlFor='website'>Website</label>
              <input
                type='text'
                id='website'
                name='website'
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete='off'
              />
            </div>

            {/* Submit Button */}
            <div className='flex items-center gap-4'>
              <button
                type='submit'
                disabled={loading || success || !canSubmit}
                className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                  loading || success || !canSubmit
                    ? "bg-gray-500 cursor-not-allowed opacity-50"
                    : "btn-cta text-white shadow-lg hover:shadow-xl"
                }`}
              >
                {loading ? (
                  <>
                    <Loader className='w-5 h-5 animate-spin' />
                    <span>Sending...</span>
                  </>
                ) : success ? (
                  <>
                    <CheckCircle className='w-5 h-5' />
                    <span>Sent!</span>
                  </>
                ) : !canSubmit ? (
                  <>
                    <Clock className='w-5 h-5' />
                    <span>Please Wait...</span>
                  </>
                ) : (
                  <>
                    <Send className='w-5 h-5' />
                    <span>Send Message</span>
                  </>
                )}
              </button>
            </div>

            {/* Security Notice */}
            <div className={`mt-4 p-3 rounded-lg ${dark ? "bg-gray-700/50" : "bg-gray-100"}`}>
              <div className='flex items-start gap-2'>
                <Shield className={`w-4 h-4 mt-0.5 ${dark ? "text-gray-400" : "text-gray-500"}`} />
                <p className={`text-xs ${dark ? "text-gray-400" : "text-gray-600"}`}>
                  Your information is protected and will only be used to respond to your inquiry. We
                  respect your privacy.
                </p>
              </div>
            </div>
          </form>
        </div>

        {/* Additional Information */}
        <div className='mt-12 grid grid-cols-1 md:grid-cols-3 gap-6'>
          {/* Response Time */}
          <div
            className={`p-6 rounded-xl border ${
              dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
            }`}
          >
            <div
              className={`p-3 rounded-full inline-block mb-4 ${
                dark ? "bg-blue-900/30" : "bg-blue-100"
              }`}
            >
              <Clock className='w-6 h-6 text-blue-500' />
            </div>
            <h3 className={`text-lg font-bold mb-2 ${dark ? "text-gray-100" : "text-gray-900"}`}>
              Quick Response
            </h3>
            <p className={`text-sm ${dark ? "text-gray-400" : "text-gray-600"}`}>
              We typically respond within 24-48 hours during business days.
            </p>
          </div>

          {/* Support */}
          <div
            className={`p-6 rounded-xl border ${
              dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
            }`}
          >
            <div
              className={`p-3 rounded-full inline-block mb-4 ${
                dark ? "bg-green-900/30" : "bg-green-100"
              }`}
            >
              <MessageSquare className='w-6 h-6 text-green-500' />
            </div>
            <h3 className={`text-lg font-bold mb-2 ${dark ? "text-gray-100" : "text-gray-900"}`}>
              Helpful Support
            </h3>
            <p className={`text-sm ${dark ? "text-gray-400" : "text-gray-600"}`}>
              Our team is here to help with any questions or concerns you may have.
            </p>
          </div>

          {/* Security */}
          <div
            className={`p-6 rounded-xl border ${
              dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
            }`}
          >
            <div
              className={`p-3 rounded-full inline-block mb-4 ${
                dark ? "bg-purple-900/30" : "bg-purple-100"
              }`}
            >
              <Shield className='w-6 h-6 text-purple-500' />
            </div>
            <h3 className={`text-lg font-bold mb-2 ${dark ? "text-gray-100" : "text-gray-900"}`}>
              Secure & Private
            </h3>
            <p className={`text-sm ${dark ? "text-gray-400" : "text-gray-600"}`}>
              Your data is encrypted and handled with the utmost care and privacy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
